import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

const OfferView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offer, setOffer] = useState(null);
  const [responding, setResponding] = useState(false);
  const [response, setResponse] = useState('');
  
  // Warning modal state
  const [showWarning, setShowWarning] = useState(false);
  const [previousAcceptances, setPreviousAcceptances] = useState([]);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid offer link - missing token');
      setLoading(false);
      return;
    }

    fetchOfferDetails();
  }, [token]);

  // Decode JWT token to get student ID
  const decodeToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.student_id;
    } catch (err) {
      console.error('Error decoding token:', err);
      return null;
    }
  };

  // Check for previous acceptances
  const checkPreviousAcceptances = async (studentId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/offers/check-previous-acceptances/${studentId}`);
      return response.data;
    } catch (err) {
      console.error('Error checking previous acceptances:', err);
      return { has_accepted_offers: false, accepted_offers: [] };
    }
  };

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      
      // First, get the offer details
      const response = await axios.get(`${BACKEND_URL}/api/offers/view/${token}`);
      setOffer(response.data.offer);
      
      // Then check for previous acceptances
      const studentId = decodeToken(token);
      if (studentId) {
        const acceptanceCheck = await checkPreviousAcceptances(studentId);
        if (acceptanceCheck.has_accepted_offers) {
          setPreviousAcceptances(acceptanceCheck.accepted_offers);
          setShowWarning(true);
        }
      }
      
    } catch (err) {
      console.error('Error fetching offer:', err);
      if (err.response?.status === 403) {
        setError(err.response?.data?.detail || 'This offer link has expired or is invalid');
      } else if (err.response?.status === 404) {
        setError('Offer not found');
      } else {
        setError('Failed to load offer details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (status) => {
    if (!['accepted', 'declined'].includes(status)) {
      setError('Invalid response');
      return;
    }

    // If they're trying to accept and have previous acceptances but haven't acknowledged, show warning
    if (status === 'accepted' && previousAcceptances.length > 0 && !warningAcknowledged) {
      setShowWarning(true);
      return;
    }

    try {
      setResponding(true);
      setError('');
      
      const response = await axios.put(`${BACKEND_URL}/api/offers/respond/${token}`, {
        status: status
      });

      setResponse(status);
      setOffer(prev => ({ ...prev, status: status }));
      
      // Show success message
      setTimeout(() => {
        navigate('/student/dashboard', { 
          state: { 
            message: `Offer ${status} successfully! You'll receive a confirmation email shortly.` 
          }
        });
      }, 3000);
      
    } catch (err) {
      console.error('Error responding to offer:', err);
      if (err.response?.status === 403) {
        setError(err.response?.data?.detail || 'This offer link has expired or is invalid');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'This offer has already been responded to');
      } else {
        setError('Failed to submit response');
      }
    } finally {
      setResponding(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading offer details...</p>
        </div>
      </div>
    );
  }

  if (error && !offer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <img
              src={nacmeLogo}
              alt="NACME Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <div className="text-red-600 text-lg mb-4">{error}</div>
            <button 
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Job Offer</h1>
                <p className="text-sm text-gray-500">from {offer?.company_name}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Return Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {response && (
          <div className={`mb-6 p-4 rounded-lg ${
            response === 'accepted' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center">
              <div className={`text-lg ${
                response === 'accepted' ? 'text-green-800' : 'text-red-800'
              }`}>
                {response === 'accepted' ? '✅' : '❌'} 
                Offer {response === 'accepted' ? 'Accepted' : 'Declined'}
              </div>
            </div>
            <p className={`mt-1 text-sm ${
              response === 'accepted' ? 'text-green-700' : 'text-red-700'
            }`}>
              {response === 'accepted' 
                ? 'Congratulations! The company will be notified of your acceptance.'
                : 'Your response has been recorded. Thank you for your consideration.'
              }
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Offer Header */}
          <div className="bg-blue-600 text-white px-6 py-8">
            <h2 className="text-3xl font-bold mb-2">{offer?.position_title}</h2>
            <p className="text-blue-100 text-lg">{offer?.company_name}</p>
            <p className="text-blue-200 text-sm mt-1">
              Offered to {offer?.student_name}
            </p>
          </div>

          {/* Offer Details */}
          <div className="px-6 py-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {offer?.compensation && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Compensation</h4>
                  <p className="text-lg text-gray-900">{offer.compensation}</p>
                </div>
              )}

              {offer?.start_date && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Start Date</h4>
                  <p className="text-lg text-gray-900">{formatDate(offer.start_date)}</p>
                </div>
              )}

              {offer?.duration && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Duration</h4>
                  <p className="text-lg text-gray-900">{offer.duration}</p>
                </div>
              )}

              {offer?.response_deadline && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Response Deadline</h4>
                  <p className={`text-lg ${
                    new Date(offer.response_deadline) < new Date() 
                      ? 'text-red-600 font-semibold' 
                      : 'text-gray-900'
                  }`}>
                    {formatDate(offer.response_deadline)}
                  </p>
                </div>
              )}
            </div>

            {offer?.position_description && (
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Position Description</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-900 whitespace-pre-wrap">{offer.position_description}</p>
                </div>
              </div>
            )}

            {offer?.custom_message && (
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Personal Message</h4>
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-gray-900 whitespace-pre-wrap italic">{offer.custom_message}</p>
                </div>
              </div>
            )}

            {/* Response Buttons */}
            {!response && offer?.status === 'pending' && (
              <div className="border-t pt-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  Please respond to this offer:
                </h4>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => handleResponse('accepted')}
                    disabled={responding}
                    className={`flex-1 py-3 px-6 text-white font-medium rounded-lg transition-colors ${
                      responding 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {responding ? 'Processing...' : '✅ Accept Offer'}
                  </button>
                  <button
                    onClick={() => handleResponse('declined')}
                    disabled={responding}
                    className={`flex-1 py-3 px-6 font-medium rounded-lg border transition-colors ${
                      responding 
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed' 
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {responding ? 'Processing...' : '❌ Decline Offer'}
                  </button>
                </div>
                
                <p className="mt-3 text-sm text-gray-600 text-center">
                  Your response will be sent directly to {offer?.company_name}
                </p>
              </div>
            )}

            {offer?.status !== 'pending' && !response && (
              <div className="border-t pt-8">
                <div className={`p-4 rounded-lg ${
                  offer.status === 'accepted' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`font-medium ${
                    offer.status === 'accepted' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {offer.status === 'accepted' ? '✅ Offer Accepted' : '❌ Offer Declined'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    offer.status === 'accepted' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    You have already responded to this offer.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  ⚠️ Multiple Offer Warning
                </h3>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p className="font-semibold text-red-600 mb-2">
                    Warning: You have already accepted an offer during the current cycle.
                  </p>
                  <p className="mb-3">
                    You may be disqualified from <strong>all</strong> internship opportunities if you accept more than one offer.
                  </p>
                  
                  {previousAcceptances.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded p-3 mb-3">
                      <p className="font-medium mb-2">Previously accepted offers:</p>
                      {previousAcceptances.map((acceptance, index) => (
                        <div key={index} className="text-xs border-l-2 border-blue-500 pl-2 mb-2">
                          <div className="font-medium">{acceptance.company_name}</div>
                          <div>{acceptance.position_title}</div>
                          <div className="text-gray-500">
                            Accepted: {new Date(acceptance.accepted_date).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    Please consider this carefully before proceeding. Contact NACME support if you have questions.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={async () => {
                  try {
                    // Record the acknowledgment in the database
                    await axios.post(`${BACKEND_URL}/api/offers/acknowledge-warning/${token}`);
                    console.log('Warning acknowledgment recorded');
                  } catch (err) {
                    console.error('Error recording warning acknowledgment:', err);
                    // Continue anyway - don't block the user for logging issues
                  }
                  
                  setWarningAcknowledged(true);
                  setShowWarning(false);
                }}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                I Understand the Risk - Proceed Anyway
              </button>
              <button
                onClick={() => {
                  setShowWarning(false);
                  navigate('/student/dashboard');
                }}
                className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel - Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferView;